import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { IResValueExistCheckParams } from '../models/res-booking.model';
import { IReservationCashReceiptCheckNO, ResCashReceipt, ResCashReceiptCashAccount, ReservationCashReceiptPayerGroup } from '../models/res-cash-receipt.model';
import { ResGoodwillCompany, ResGoodwillDefaultPriceType, ResGoodwillDefaultPrices, ResGoodwillRMTypes } from '../models/res-goodwill-deposit.model';
import { IReservationApiCreateCashReceiptParams, IResTransferCashParams, ResBookingPaymentDetailPayer, ResBookingPaymentDetails, ResPaymentRefundInfo } from '../models/res-payment.model';
import { IResApiGetHeritageDepositParams, ResHeritageCurrency, ResHeritagePrice, ResHeritageTier } from '../models/res-heritage-deposit.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationCashApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  public createCashReceipt(params: IReservationApiCreateCashReceiptParams): Observable<any> {
    return this.postDynamicAPI('cashReceipt/create', params);
  }

  /**
   * Checks is params.Value is a valid and existing entity
   * The entity depends on params.Type:
   * BkgID -> booking and GroupCod -> Group
   *
   * @param {IResValueExistCheckParams} params
   * @return {Observable<boolean>}
   * @memberof ReservationCashApiService
   */
  public existsValue(params: IResValueExistCheckParams): Observable<boolean> {
    return this.getDynamicAPI('utility/exists', params).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        let check = false;

        if (Array.isArray(response.body) && response.body.length > 0) {
          const retValue: { Valid: string } = response.body[0];
          check = retValue.Valid === 'Y';
        }

        return check;
      })
    );
  }

  getBookingPaymentDetailsPayers(orderID: string, requestPaymentActionID:number): Observable<ResBookingPaymentDetailPayer[]> {
    return this.callDynamicAPI('payment/getRequestDetails', { OrderID: orderID, RequestPaymentActionID: requestPaymentActionID === 0 ? undefined : requestPaymentActionID }).pipe(
      map<DynamicApiResponse, ResBookingPaymentDetailPayer[]>((response) => {
        const info = new ResBookingPaymentDetails(this.getBookingPaymentDetailsMappingData(response.body))
        return info.payers
      })
    );
  }

  public getCashAccounts(params: { CurrencyCod: string, ReceiptTypeID: number, DebitCredit?: string, BkgID?: number }): Observable<Array<ResCashReceiptCashAccount>> {
    return this.callDynamicAPI('cashAccounts/select', params).pipe(
      map<DynamicApiResponse, Array<ResCashReceiptCashAccount>>((response) => {
        return response.body.map((r: any) => new ResCashReceiptCashAccount(r));
      })
    );
  }

  public getCashReceiptsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ResCashReceipt>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.getDynamicAPI('cashReceipt/select', params)).then((response: any) => {
      return {
        data: response.body.map((cashReceipt: any) => new ResCashReceipt(cashReceipt)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  public getCheckNo(): Observable<Array<IReservationCashReceiptCheckNO>> {
    return this.callDynamicAPI('checkNO/select').pipe(
      map<DynamicApiResponse, Array<IReservationCashReceiptCheckNO>>((response) => {
        return response.body;
      })
    );
  }

  public getDeposit(params: any): Observable<any> {
    return this.callDynamicAPI('OpenDeposit/get', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  getGoodwillDepositParams(): Observable<{ RMTypes: ResGoodwillRMTypes[], pricesAndCurrencies: ResGoodwillDefaultPrices[], priceTypes: ResGoodwillDefaultPriceType[], companies: ResGoodwillCompany[] }> {
    return this.callDynamicAPI('bkg/getGoodWillParams').pipe(
      map<DynamicApiResponse, { RMTypes: ResGoodwillRMTypes[], pricesAndCurrencies: ResGoodwillDefaultPrices[], priceTypes: ResGoodwillDefaultPriceType[], companies: ResGoodwillCompany[] }>((response) => {
        return {
          RMTypes: response.body.DT0.map((data: any) => new ResGoodwillRMTypes(data)),
          pricesAndCurrencies: response.body.DT1.map((data: any) => new ResGoodwillDefaultPrices(data)),
          priceTypes: response.body.DT2.map((data: any) => new ResGoodwillDefaultPriceType(data)),
          companies: response.body.DT3.map((data: any) => new ResGoodwillCompany(data))
        };
      })
    )
  }

  getHeritageDepositParams(params?: IResApiGetHeritageDepositParams): Observable<{ priceTypes: ResHeritageTier[], currencies: ResHeritageCurrency[], prices: ResHeritagePrice[] }> {
    return this.callDynamicAPI('bkg/getHeritageParams', params).pipe(
      map<DynamicApiResponse, { priceTypes: ResHeritageTier[], currencies: ResHeritageCurrency[], prices: ResHeritagePrice[] }>((response) => {
        return {
          priceTypes: response.body.DT0.map((data: any) => new ResHeritageTier(data)),
          currencies: response.body.DT1.map((data: any) => new ResHeritageCurrency(data)),
          prices: response.body.DT2.map((data: any) => new ResHeritagePrice(data))
        };
      })
    )
  }

  public getPayerGroup(bkgId: number): Observable<ReservationCashReceiptPayerGroup[]> {
    return this.callDynamicAPI('payerGroup/select', { BkgID: bkgId }).pipe(
      map<DynamicApiResponse, ReservationCashReceiptPayerGroup[]>((response) => {
        return response.body.map((pg: any) => new ReservationCashReceiptPayerGroup(pg));
      })
    );
  }

  getPaymentDisclaimerMessage(): Observable<string> {
    const params = {
      BkgID: 0,
      Amount: 0,
      Currency: '',
      EntityId: 0
    }
    return this.getDynamicAPI('bkg/PaymentDisclaimer', params).pipe(
      map<DynamicApiResponse, string>((response) => {
        return response.body[0].HTML
      })
    );
  }

  getPaymentRefundInfo(bkgID: number,orderID?: string): Observable<ResPaymentRefundInfo> {
    return this.getDynamicAPI('payment/refundInfo', { BkgID: bkgID, OrderID: orderID }).pipe(
      map<DynamicApiResponse, ResPaymentRefundInfo>((response) => {
        const refundInfo:any = response.body.DT0?.[0] ?? {};
        refundInfo.Details = response.body.DT1 ?? []
        return new ResPaymentRefundInfo(refundInfo);
      })
    );
  }

  /**
   * Does a transfer cash between bookings or groups
   *
   * @param {IResTransferCashParams} params
   * @return {Observable<{result: boolean, message: string}>}
   * @memberof ReservationCashApiService
   */
  public transferCash(params: IResTransferCashParams[]): Observable<{ result: boolean; message: string }> {
    return this.postDynamicAPI('booking/transferCash', { Transfer: params }).pipe(
      map<DynamicApiResponse, { result: boolean; message: string }>((response) => {
        let retValue: { result: boolean; message: string } = { result: false, message: '' };

        if (Array.isArray(response.body) && response.body.length > 0) {
          const responseValue: { Result: string; Message: string } = response.body[0];
          retValue.result = responseValue.Result === 'OK';
          retValue.message = responseValue.Message;
        }

        return retValue;
      })
    );
  }

  getBookingPaymentDetailsMappingData(body:any) {
    const data:any = body.DT0?.[0] ?? {}
    data.Payers = (body.DT1 ?? []).map((payer:any)=>{
      let currency = data.CurrencyCod ?? ''
      const otherCurrency = data.OtherCurrency ?? currency
      let fxRate = 1
      if( otherCurrency !== currency) {
        currency = otherCurrency
        fxRate = data.FxRate ?? 1
      }
      return Object.assign({
        PayerName: data.PayerName ?? '',
        PaymentCurrency: currency,
        FxRate: fxRate,
        BkgCurrency: data.CurrencyCod ?? ''
      },payer)
    })
    return data
  }
}
